import type { SubscriptionStatus } from "@/api/types";
import { addDays, today } from "@/lib/dates";

/** What RigoSub calls Accounting's plans (suite ADR-0096). */
export const PLAN_NAMES: Record<string, string> = {
  STARTER: "Starter",
  STANDARD: "Standard",
  PRO: "Pro",
};

export const CYCLE_NAMES: Record<string, string> = {
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
  YEARLY: "annual",
};

/** A feature a plan holds back: where it lives in the app, and the lowest plan that has it. */
export interface PlanFeature {
  code: string;
  name: string;
  /** What it does, for the page that says it isn't on this plan. */
  description: string;
  plan: string;
  /** The app's routes it covers. */
  paths: string[];
}

export const PLAN_FEATURES: PlanFeature[] = [
  {
    code: "QUOTATIONS",
    name: "Quotations",
    description: "Send a customer a quotation, and turn it into an invoice when they accept.",
    plan: "Standard",
    paths: ["/sales/quotations"],
  },
  {
    code: "PURCHASE_ORDERS",
    name: "Purchase orders",
    description: "Order from a supplier, send the order, and match the bill to it when it comes.",
    plan: "Standard",
    paths: ["/purchases/orders"],
  },
  {
    code: "RECONCILIATION",
    name: "Bank reconciliation",
    description: "Import a bank statement and match each line to what's in the books.",
    plan: "Standard",
    paths: ["/banking/reconciliation"],
  },
  {
    code: "TWO_STEP_APPROVALS",
    name: "Two-step approvals",
    description: "Have a document verified by one person and approved by another.",
    plan: "Pro",
    paths: [],
  },
];

/** Whether the plan allows a feature. No list is no instruction, so nothing is held back. */
export const allows = (status: SubscriptionStatus | undefined, code: string): boolean =>
  !status?.features || status.features.includes(code);

/** The feature a path belongs to, if a plan can hold it back. */
export const featureAt = (pathname: string): PlanFeature | undefined =>
  PLAN_FEATURES.find((feature) =>
    feature.paths.some((path) => pathname === path || pathname.startsWith(`${path}/`)),
  );

export const featureNamed = (code: string): PlanFeature | undefined =>
  PLAN_FEATURES.find((feature) => feature.code === code);

/** Days left in a trial, counting today and its last day. */
export function trialDaysLeft(trialEndsOn: string, on: string = today()): number {
  let days = 0;
  for (let day = on; day <= trialEndsOn && days < 400; day = addDays(day, 1)) days++;
  return days;
}

/** The account menu's plan line: "Pro trial · ends 17 Nov 2026" or "Standard · monthly". */
export function planLine(
  status: SubscriptionStatus | undefined,
  format: (iso: string) => string,
): string | null {
  if (!status?.enabled || !status.planCode) return null;
  const plan = PLAN_NAMES[status.planCode] ?? status.planCode;
  if (status.trialEndsOn) return `${plan} trial · ends ${format(status.trialEndsOn)}`;
  const cycle = status.cycle ? CYCLE_NAMES[status.cycle] : undefined;
  return cycle ? `${plan} · ${cycle}` : plan;
}
